import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { Store } from "@tldraw/store";
import {
  registerColorsFromThemes,
  registerFontsFromThemes
} from "@tldraw/tlschema";
import { annotateError } from "@tldraw/utils";
import classNames from "classnames";
import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore
} from "react";
import { version } from "../version.mjs";
import { DefaultErrorFallback } from "./components/default-components/DefaultErrorFallback.mjs";
import { EditorPortalProvider } from "./components/EditorPortal.mjs";
import { OptionalErrorBoundary } from "./components/ErrorBoundary.mjs";
import { createTLCurrentUser } from "./config/createTLCurrentUser.mjs";
import { Editor } from "./editor/Editor.mjs";
import { resolveThemes } from "./editor/managers/ThemeManager/ThemeManager.mjs";
import { useEditorComponents } from "./hooks/EditorComponentsContext.mjs";
import { ContainerProvider, useContainer } from "./hooks/useContainer.mjs";
import { useCursor } from "./hooks/useCursor.mjs";
import { useDarkMode } from "./hooks/useDarkMode.mjs";
import { EditorProvider, useEditor } from "./hooks/useEditor.mjs";
import { EditorComponentsProvider } from "./hooks/useEditorComponents.mjs";
import { useEvent } from "./hooks/useEvent.mjs";
import { useForceUpdate } from "./hooks/useForceUpdate.mjs";
import { useShallowObjectIdentity } from "./hooks/useIdentity.mjs";
import { useLocalStore } from "./hooks/useLocalStore.mjs";
import { useRefState } from "./hooks/useRefState.mjs";
import { useStateAttribute } from "./hooks/useStateAttribute.mjs";
import { useZoomCss } from "./hooks/useZoomCss.mjs";
import { LicenseProvider } from "./license/LicenseProvider.mjs";
import { Watermark } from "./license/Watermark.mjs";
import { getGlobalDocument } from "./utils/dom.mjs";
const EMPTY_SHAPE_UTILS_ARRAY = [];
const EMPTY_BINDING_UTILS_ARRAY = [];
const EMPTY_ASSET_UTILS_ARRAY = [];
const EMPTY_TOOLS_ARRAY = [];
const TL_CONTAINER_CLASS = "tl-container";
const TldrawEditor = memo(function TldrawEditor2({
  store,
  components,
  className,
  user: _user,
  options: _options,
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  textOptions: _textOptions,
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  deepLinks: _deepLinks,
  ...rest
}) {
  const resolvedThemes = resolveThemes(rest.themes);
  registerColorsFromThemes(resolvedThemes);
  registerFontsFromThemes(resolvedThemes);
  const [container, setContainer] = useState(null);
  const [portalHost, setPortalHost] = useState(null);
  const user = useMemo(() => _user ?? createTLCurrentUser(), [_user]);
  const ErrorFallback = components?.ErrorFallback === void 0 ? DefaultErrorFallback : components?.ErrorFallback;
  const mergedOptions = useMemo(() => {
    let result = _options;
    if (_textOptions) {
      result = { ...result, text: result?.text ?? _textOptions };
    }
    if (_deepLinks !== void 0) {
      result = { ...result, deepLinks: result?.deepLinks ?? _deepLinks };
    }
    return result;
  }, [_options, _textOptions, _deepLinks]);
  const withDefaults = {
    ...rest,
    shapeUtils: rest.shapeUtils ?? EMPTY_SHAPE_UTILS_ARRAY,
    bindingUtils: rest.bindingUtils ?? EMPTY_BINDING_UTILS_ARRAY,
    assetUtils: rest.assetUtils ?? EMPTY_ASSET_UTILS_ARRAY,
    tools: rest.tools ?? EMPTY_TOOLS_ARRAY,
    components,
    options: useShallowObjectIdentity(mergedOptions)
  };
  return (
    /* @__PURE__ */ jsxs(
      "div",
      {
        ref: setContainer,
        "data-tldraw": version,
        draggable: false,
        className: classNames(`${TL_CONTAINER_CLASS} tl-theme__light`, className),
        tabIndex: -1,
        role: "application",
        "aria-label": _options?.branding ?? "tldraw",
        children: [
          /* @__PURE__ */ jsx(
            OptionalErrorBoundary,
            {
              fallback: ErrorFallback,
              onError: (error) => annotateError(error, { tags: { origin: "react.tldraw-before-app" } }),
              children: container && /* @__PURE__ */ jsx(LicenseProvider, { licenseKey: rest.licenseKey, children: /* @__PURE__ */ jsx(ContainerProvider, { container, children: /* @__PURE__ */ jsx(EditorPortalProvider, { host: portalHost, children: /* @__PURE__ */ jsx(EditorComponentsProvider, { overrides: components, children: store ? store instanceof Store ? (
                // Store is ready to go, whether externally synced or not
                /* @__PURE__ */ (jsx(TldrawEditorWithReadyStore, { ...withDefaults, store, user }))
              ) : (
                // Store is a synced store, so handle syncing stages internally
                /* @__PURE__ */ (jsx(TldrawEditorWithLoadingStore, { ...withDefaults, store, user }))
              ) : (
                // We have no store (it's undefined) so create one and possibly sync it
                /* @__PURE__ */ (jsx(TldrawEditorWithOwnStore, { ...withDefaults, store, user }))
              ) }) }) }) })
            }
          ),
          /* @__PURE__ */ jsx("div", { className: "tl-portal-host", ref: setPortalHost })
        ]
      }
    )
  );
});
function TldrawEditorWithOwnStore(props) {
  const {
    defaultName,
    snapshot,
    initialData,
    shapeUtils,
    bindingUtils,
    assetUtils,
    persistenceKey,
    sessionId,
    user,
    assets,
    users,
    migrations,
    themes
  } = props;
  const syncedStore = useLocalStore({
    shapeUtils,
    bindingUtils,
    assetUtils,
    initialData,
    persistenceKey,
    sessionId,
    defaultName,
    snapshot,
    assets,
    users,
    migrations,
    themes
  });
  return /* @__PURE__ */ jsx(TldrawEditorWithLoadingStore, { ...props, store: syncedStore, user });
}
const TldrawEditorWithLoadingStore = memo(function TldrawEditorBeforeLoading({
  store,
  user,
  ...rest
}) {
  const container = useContainer();
  useLayoutEffect(() => {
    if (user.userPreferences.get().colorScheme === "dark") {
      container.classList.remove("tl-theme__light");
      container.classList.add("tl-theme__dark");
    }
  }, [container, user]);
  const { LoadingScreen: LoadingScreen2 } = useEditorComponents();
  switch (store.status) {
    case "error": {
      throw store.error;
    }
    case "loading": {
      return LoadingScreen2 ? /* @__PURE__ */ jsx(LoadingScreen2, {}) : null;
    }
    case "not-synced": {
      break;
    }
    case "synced-local": {
      break;
    }
    case "synced-remote": {
      break;
    }
  }
  return /* @__PURE__ */ jsx(TldrawEditorWithReadyStore, { ...rest, store: store.store, user });
});
const noAutoFocus = () => getGlobalDocument().location.search.includes("tldraw_preserve_focus");
function TldrawEditorWithReadyStore({
  onMount,
  children,
  store,
  tools,
  shapeUtils,
  bindingUtils,
  assetUtils,
  overlayUtils,
  user,
  initialState,
  autoFocus = true,
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  cameraOptions,
  options,
  licenseKey,
  getShapeVisibility,
  colorScheme,
  assetUrls,
  themes,
  initialTheme
}) {
  const { ErrorFallback } = useEditorComponents();
  const container = useContainer();
  const [editor, setEditor] = useRefState(null);
  const canvasRef = useRef(null);
  const _deepLinks = options?.deepLinks;
  const deepLinks = useShallowObjectIdentity(_deepLinks === true ? {} : _deepLinks);
  const editorOptionsRef = useRef({
    // for these, it's because they're only used when the editor first mounts:
    autoFocus: autoFocus && !noAutoFocus(),
    initialState,
    colorScheme,
    // for these, it's because we keep them up to date in a separate effect:
    cameraOptions,
    deepLinks,
    themes,
    initialTheme
  });
  useLayoutEffect(() => {
    editorOptionsRef.current = {
      autoFocus: autoFocus && !noAutoFocus(),
      initialState,
      colorScheme,
      cameraOptions,
      deepLinks,
      themes,
      initialTheme
    };
  }, [autoFocus, initialState, colorScheme, cameraOptions, deepLinks, themes, initialTheme]);
  useLayoutEffect(
    () => {
      const {
        autoFocus: autoFocus2,
        initialState: initialState2,
        colorScheme: initColorScheme,
        cameraOptions: cameraOptions2,
        deepLinks: deepLinks2,
        themes: themes2,
        initialTheme: initialTheme2
      } = editorOptionsRef.current;
      const editor2 = new Editor({
        store,
        shapeUtils,
        bindingUtils,
        assetUtils,
        overlayUtils,
        tools,
        getContainer: () => container,
        user,
        initialState: initialState2,
        // we should check for some kind of query parameter that turns off autofocus
        autoFocus: autoFocus2,
        cameraOptions: cameraOptions2,
        options,
        licenseKey,
        getShapeVisibility,
        colorScheme: initColorScheme,
        fontAssetUrls: assetUrls?.fonts,
        themes: themes2,
        initialTheme: initialTheme2
      });
      editor2.updateViewportScreenBounds(canvasRef.current ?? container);
      if (deepLinks2) {
        if (!deepLinks2?.getUrl) {
          editor2.navigateToDeepLink(deepLinks2);
        } else {
          editor2.navigateToDeepLink({ ...deepLinks2, url: deepLinks2.getUrl(editor2) });
        }
      }
      setEditor(editor2);
      return () => {
        editor2.dispose();
      };
    },
    // if any of these change, we need to recreate the editor.
    [
      assetUtils,
      bindingUtils,
      colorScheme,
      container,
      options,
      overlayUtils,
      shapeUtils,
      store,
      tools,
      user,
      setEditor,
      licenseKey,
      getShapeVisibility,
      assetUrls
    ]
  );
  useLayoutEffect(() => {
    if (!editor) return;
    if (deepLinks) {
      return editor.registerDeepLinkListener(deepLinks);
    }
  }, [editor, deepLinks]);
  useLayoutEffect(() => {
    if (editor && (cameraOptions || options?.camera)) {
      editor.setCameraOptions({ ...cameraOptions, ...options?.camera });
    }
  }, [editor, cameraOptions, options?.camera]);
  useLayoutEffect(() => {
    if (editor && themes) {
      for (const def of Object.values(themes)) {
        editor.updateTheme(def);
      }
    }
  }, [editor, themes]);
  useLayoutEffect(() => {
    if (editor && initialTheme) {
      editor.setCurrentTheme(initialTheme);
    }
  }, [editor, initialTheme]);
  const crashingError = useSyncExternalStore(
    useCallback(
      (onStoreChange) => {
        if (editor) {
          editor.on("crash", onStoreChange);
          return () => editor.off("crash", onStoreChange);
        }
        return () => {
        };
      },
      [editor]
    ),
    () => editor?.getCrashingError() ?? null
  );
  useEffect(
    function handleFocusOnPointerDownForPreserveFocusMode() {
      if (!editor) return;
      function handleFocusOnPointerDown() {
        if (!editor) return;
        editor.focus();
      }
      function handleBlurOnPointerDown() {
        if (!editor) return;
        editor.blur();
      }
      if (autoFocus && noAutoFocus()) {
        const container2 = editor.getContainer();
        container2.addEventListener("pointerdown", handleFocusOnPointerDown);
        container2.ownerDocument.body.addEventListener("pointerdown", handleBlurOnPointerDown);
        return () => {
          container2.removeEventListener("pointerdown", handleFocusOnPointerDown);
          container2.ownerDocument.body.removeEventListener("pointerdown", handleBlurOnPointerDown);
        };
      }
    },
    [editor, autoFocus]
  );
  const [_fontLoadingState, setFontLoadingState] = useState(null);
  let fontLoadingState = _fontLoadingState;
  if (editor !== fontLoadingState?.editor) {
    fontLoadingState = null;
  }
  useLayoutEffect(() => {
    if (!editor) return;
    if (editor.options.maxFontsToLoadBeforeRender === 0) {
      setFontLoadingState({ editor, isLoaded: true });
      return;
    }
    let isCancelled = false;
    setFontLoadingState({ editor, isLoaded: false });
    editor.fonts.loadRequiredFontsForCurrentPage(editor.options.maxFontsToLoadBeforeRender).finally(() => {
      if (isCancelled) return;
      setFontLoadingState({ editor, isLoaded: true });
    });
    return () => {
      isCancelled = true;
    };
  }, [editor]);
  const { Canvas, LoadingScreen: LoadingScreen2 } = useEditorComponents();
  if (!editor || !fontLoadingState?.isLoaded) {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      LoadingScreen2 && /* @__PURE__ */ jsx(LoadingScreen2, {}),
      /* @__PURE__ */ jsx("div", { className: "tl-canvas", ref: canvasRef })
    ] });
  }
  return (
    // the top-level tldraw component also renders an error boundary almost
    // identical to this one. the reason we have two is because this one has
    // access to `App`, which means that here we can enrich errors with data
    // from app for reporting, and also still attempt to render the user's
    // document in the event of an error to reassure them that their work is
    // not lost.
    /* @__PURE__ */ (jsx(OptionalErrorBoundary, {
      fallback: ErrorFallback,
      onError: (error) => editor.annotateError(error, { origin: "react.tldraw", willCrashApp: true }),
      children: crashingError ? /* @__PURE__ */ jsx(Crash, { crashingError }) : /* @__PURE__ */ jsx(EditorProvider, { editor, children: /* @__PURE__ */ jsxs(Layout, { onMount, children: [
        children ?? (Canvas ? /* @__PURE__ */ jsx(Canvas, {}, editor.contextId) : null),
        /* @__PURE__ */ jsx(Watermark, {})
      ] }) })
    }))
  );
}
function Layout({ children, onMount }) {
  useZoomCss();
  useCursor();
  useDarkMode();
  useForceUpdate();
  useStateAttribute();
  useOnMount((editor) => {
    const teardownStore = editor.store.props.onMount(editor);
    const teardownCallback = onMount?.(editor);
    return () => {
      teardownStore?.();
      teardownCallback?.();
    };
  });
  return children;
}
function Crash({ crashingError }) {
  throw crashingError;
}
function LoadingScreen({ children }) {
  return /* @__PURE__ */ jsx("div", { className: "tl-loading", "aria-busy": "true", tabIndex: 0, children });
}
function ErrorScreen({ children }) {
  return /* @__PURE__ */ jsx("div", { className: "tl-loading", children });
}
function useOnMount(onMount) {
  const editor = useEditor();
  const onMountEvent = useEvent((editor2) => {
    let teardown = void 0;
    editor2.run(
      () => {
        teardown = onMount?.(editor2);
        editor2.emit("mount");
      },
      { history: "ignore" }
    );
    window.tldrawReady = true;
    return () => {
      teardown?.();
      if (editor2.getIsMounted() && !editor2.isDisposed) {
        editor2.run(() => editor2.emit("unmount"), { history: "ignore" });
      }
    };
  });
  React.useLayoutEffect(() => {
    if (editor) return onMountEvent?.(editor);
  }, [editor, onMountEvent]);
}
export {
  ErrorScreen,
  LoadingScreen,
  TL_CONTAINER_CLASS,
  TldrawEditor,
  useOnMount
};
//# sourceMappingURL=TldrawEditor.mjs.map
